import React, {useEffect, useMemo, useRef, useState} from "react";
import {createUUId, deepCopy, deepEqual} from "../../../../../core/services/utils/utils";
import useIsMounted from "../../../../hooks/use-is-mounted";
import {readString} from "react-papaparse";
import DropZone from "../../../base/dropzone";
import {ReactComponent as ImagePlaceholder} from "../../../../../assets/images/dropzone/upload-placeholder.svg";
import {InputFileAcceptTypes} from "../../../../../core/constants/enums";

// TODO: change the usages of this dropzone to the new format

/**
 * A dropzone that accepts csv files and parses them.
 * @param accept
 * @param onParsed
 * @param {Array<{
 *     name: string,
 *     title: string,
 *     generator: function(number): any,
 * }>} requiredFields
 * @return {JSX.Element}
 * @constructor
 */
const ImportCsvFileDropzone = ({
                                   accept = [InputFileAcceptTypes.csv],
                                   onParsed,
                                   requiredFields,
                               }) => {
    const requiredFieldsTitlesList = useMemo(() => requiredFields.map(e => e.title), [requiredFields]);
    const idRef = useRef(createUUId(true));
    const [parseError, setParseError] = useState(null);
    const [parsing, setParsing] = useState(false);
    const isMounted = useIsMounted();

    /**
     * With each change in the parseError state:
     * - if set, removes the error after 2 second timeout.
     */
    useEffect(() => {
        if (!parseError)
            return;
        const timer = setTimeout(() => {
            setParseError(null);
        }, 2000)
        return () => clearTimeout(timer);
    }, [parseError])

    /**
     * Validates and parses the provided csv file.
     *
     * * if the file is parsed properly, calls the onParsed callback with the parsed file as its arg.
     * @param files {File[] | null}
     */
    const onFileSelected = async (files) => {
        setParseError(null);
        const file = files[0];
        setParsing(true);
        const parsed = await validateAndParseFile(file);
        if (!isMounted())
            return;
        setParsing(false);
        if (parsed) {
            onParsed(parsed);
        }
    }

    /**
     * Validates the parses the given csv file.
     *
     * * the file must have at least one row of entries
     * * the file's columns must at least contain the entries in the [requiredFields] list.
     * @param file
     * @return {Promise<Record<string, string>[] | void>}
     */
    const validateAndParseFile = async (file) => {
        try {
            const parsed = readString(file, {header: false});
            let columns = Array.from((parsed?.data ? parsed?.data[0] : []) ?? [])
            let rows = deepCopy(parsed?.data?.slice(1) ?? []);
            columns = columns?.filter((e, i) => {
                const retain = !!e;
                if (!retain)
                    rows.splice(i, 1);
                return retain;
            });

            // validate
            if (rows?.length < 1)
                return setParseError('The chosen CSV file does not contain any values.');
            if (columns?.filter(e => requiredFieldsTitlesList.includes(e))?.length < requiredFieldsTitlesList.length) {
                const requiredHeadersString = requiredFieldsTitlesList?.reduce(
                    (p, c, i, a) => p.concat(c + (i + 1 === a.length ? '' : ', ')), ''
                );
                return setParseError(`The provided CSV file must contain the following headers:\n ${requiredHeadersString}`)
            }

            // sort the given data frame.
            [columns, rows] = sortImportedEntriesByColumns(columns, rows);
            // parse the data frame
            return parseImportedEntries(columns, rows);
        } catch (e) {
            setParseError('Error while parsing the provided CSV file.');
        }
    }

    /**
     * Sorts the given columns and rows of the data frame based on the order of the required fields.
     *
     * @param {string[]} columns
     * @param {string[][]} rows
     * @return {[string[],string[][]]}
     */
    const sortImportedEntriesByColumns = (columns, rows) => {
        const headerCorrectIndices = requiredFieldsTitlesList.map(e => columns?.findIndex(f => f === e)).filter(e => e !== -1);
        if (!deepEqual(Array(columns).fill(null).map((_, e) => e), headerCorrectIndices)) {
            const swapped = [];
            for (let currentIndex = 0; currentIndex < headerCorrectIndices.length; currentIndex++) {
                const correctIndex = headerCorrectIndices[currentIndex];
                const alreadySwapped = !!swapped.find(e => deepEqual(e, [currentIndex, correctIndex]));
                if (currentIndex === correctIndex || alreadySwapped) {
                    continue;
                }
                swapped.push([currentIndex, correctIndex], [correctIndex, currentIndex]);
                [columns[currentIndex], columns[correctIndex]] = [columns[correctIndex], columns[currentIndex]]
                rows.forEach(row => ([row[currentIndex], row[correctIndex]] = [row[correctIndex], row[currentIndex]] || void 0))
            }
            return [columns, rows];
        }
        return [columns, rows];
    }

    /**
     * Generates an object from the given columns and rows that is to be used by the parent component.
     *
     * @param {string[]} columns
     * @param {string[][]} rows
     * @return {Record<string, string>[]}
     */
    const parseImportedEntries = (columns, rows) => {
        return rows.map(row =>
            Object.fromEntries(
                columns.map((column, i) =>
                    [requiredFields.find(e => e.title === column)?.name, row[i]]
                )
            )
        )
    }

    return (
        <>
            <DropZone
                disabled={parsing}
                identifier={'import-products-fast-connection'}
                accept={accept}
                id={idRef.current}
                setLoading={() => (void 0)}
                onSuccess={onFileSelected}
                error={!!parseError}
                multiple={false}
                openOnClick
            >
                {() =>
                    <div className={'import-products-fast-connection-content'}>
                        <p className={'placeholder-text'}>
                            Upload your csv file here by clicking or dragging and dropping.
                        </p>
                        <ImagePlaceholder className={'placeholder-image'}/>
                        {
                            parseError
                                ? <p className={'placeholder-text'}>
                                    {parseError}
                                </p>
                                : <></>
                        }

                    </div>
                }
            </DropZone>
        </>
    )
}

export default ImportCsvFileDropzone;
